/* eslint-disable no-nested-ternary */
import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AppCheckBox, AppPrimaryButton } from 'shared/components/AppComponents';
import { ReactComponent as Home } from 'assets/images/ic_home.svg';
import { ReactComponent as Globe } from 'assets/images/ic_globe.svg';
import { ReactComponent as Back } from 'assets/images/ic_back.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from 'shared/components/PageHeader';
import { INTERNAL_TYPES, THIRD_PARTY_TYPES } from 'shared/Constants';

const AddAISolutions = (): ReactElement => {
  const navigation = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const [sourceType, setSourceType] = useState('internal');
  const [internalType, setInternalType] = useState(0);
  const [thirdPartyType, setThirdPartyType] = useState(0);
  const match = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (location.state) {
      const {
        internalType: _internalType,
        thirdPartyType: _thirdPartyType,
        sourceType: _sourceType,
      } = location.state;
      setInternalType(_internalType);
      setThirdPartyType(_thirdPartyType);
      setSourceType(_sourceType);
    }
  }, [location.state]);

  return (
    <>
      <PageHeader
        title="Add new AI solution"
        description="Start by selecting whether this AI solution was developed in-house or provided by a third-party vendor. This helps us classify it correctly and apply the right governance controls."
      />
      <Box className="ai-inventory ai-solutions">
        <HeadTag title="AI Systems" />

        <Box className="ai-solutions add">
          <Stack spacing={4} direction="column">
            <Box className="source-type-heading">
              <Typography variant="h4">Select Source Type</Typography>
              <Typography component="span" variant="body1">
                Choose how this AI system was created or acquired
              </Typography>
            </Box>
            <Stack direction={match ? 'column' : 'row'} spacing={2}>
              <Box
                className="selection-type"
                component="button"
                onClick={() => {
                  setSourceType('internal');
                  setThirdPartyType(0);
                }}
              >
                <Stack direction="row" spacing={1}>
                  <AppCheckBox
                    name="internalAI"
                    onChange={() => {
                      setSourceType('internal');
                      setThirdPartyType(0);
                    }}
                    checked={sourceType === 'internal'}
                  />
                  <SvgIcon
                    component={Home}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#3D3C42"
                  />
                  <Typography variant="h6">
                    Internal AI (built in-house)
                  </Typography>
                </Stack>
                <Typography variant="body2" component="span">
                  Built in-house by your organisation
                </Typography>
              </Box>
              <Box
                className="selection-type"
                component="button"
                onClick={() => {
                  setSourceType('third-party');
                  setInternalType(0);
                }}
              >
                <Stack direction="row" spacing={1}>
                  <AppCheckBox
                    name="thirdPartyAI"
                    checked={sourceType === 'third-party'}
                    onChange={() => {
                      setSourceType('third-party');
                      setInternalType(0);
                    }}
                  />
                  <SvgIcon
                    component={Globe}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#3D3C42"
                  />
                  <Typography variant="h6">
                    Third-Party AI (external vendors)
                  </Typography>
                </Stack>
                <Typography variant="body2" component="span">
                  Provided by an external vendor
                </Typography>
              </Box>
            </Stack>
            <Box className="select-classification">
              <Stack direction={match ? 'column' : 'row'} spacing={1}>
                <Box className="select-heading">
                  <Typography variant="h5">Select Classification</Typography>
                  <Typography component="span" variant="body1">
                    Choose the category that best describes this AI system. The
                    list will update based on your selected source type.
                  </Typography>
                </Box>
                <AppPrimaryButton
                  title="Return on records"
                  startIcon={
                    <SvgIcon
                      component={Back}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="white"
                    />
                  }
                  onClick={() => {
                    navigation('/ai-systems');
                  }}
                />
              </Stack>
              <Box className="classification-records">
                {sourceType === 'internal' && (
                  <>
                    {INTERNAL_TYPES.map((row, index) => (
                      <Card
                        variant="outlined"
                        onClick={() => {
                          setInternalType(index + 1);
                        }}
                      >
                        <CardHeader
                          avatar={
                            <AppCheckBox
                              onChange={() => {
                                setInternalType(index + 1);
                              }}
                              checked={internalType === index + 1}
                            />
                          }
                          title={row.title}
                        />
                        <CardContent>
                          <CardActionArea
                            disableTouchRipple
                            disableRipple
                            sx={{
                              '.MuiCardActionArea-focusHighlight': {
                                background: 'transparent',
                              },
                            }}
                          >
                            <Typography component="span">
                              {row.description}
                            </Typography>
                          </CardActionArea>
                        </CardContent>
                      </Card>
                    ))}
                  </>
                )}
                {sourceType === 'third-party' && (
                  <>
                    {THIRD_PARTY_TYPES.map((row, index) => (
                      <Card
                        variant="outlined"
                        onClick={() => {
                          setThirdPartyType(index + 1);
                        }}
                      >
                        <CardHeader
                          avatar={
                            <AppCheckBox
                              onChange={() => {
                                setThirdPartyType(index + 1);
                              }}
                              checked={thirdPartyType === index + 1}
                            />
                          }
                          title={row.title}
                        />
                        <CardContent>
                          <CardActionArea
                            disableTouchRipple
                            disableRipple
                            sx={{
                              '.MuiCardActionArea-focusHighlight': {
                                background: 'transparent',
                              },
                            }}
                          >
                            <Typography component="span">
                              {row.description}
                            </Typography>
                          </CardActionArea>
                        </CardContent>
                      </Card>
                    ))}
                  </>
                )}
              </Box>
              <Stack direction="row" justifyContent="flex-start">
                <Box />
                <AppPrimaryButton
                  title="Continue"
                  disabled={internalType === 0 && thirdPartyType === 0}
                  onClick={() => {
                    navigation('/ai-systems/form-source-type', {
                      state: {
                        internalType,
                        thirdPartyType,
                        sourceType,
                      },
                    });
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default AddAISolutions;
